import { apiService } from '@shared/services/apiServices';
import type {
  ListOffersResponse,
  ListOffersParams,
  OfferListItem,
  OfferSubscriptionPlanRef,
} from '../types/offerListType';

const OFFERS_LIST_ENDPOINT = 'offer-management/offers';

/** API returns snake_case; support both for robustness */
function getStr(
  raw: Record<string, unknown>,
  camel: string,
  snake: string
): string {
  const v = raw[camel] ?? raw[snake];
  return v != null ? String(v) : '';
}

function getNum(
  raw: Record<string, unknown>,
  camel: string,
  snake: string
): number {
  const v = raw[camel] ?? raw[snake];
  if (v == null) {
    return 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getBool(
  raw: Record<string, unknown>,
  camel: string,
  snake: string
): boolean {
  const v = raw[camel] ?? raw[snake];
  return v === true || v === 'true';
}

/**
 * Normalize API item to OfferListItem.
 * API returns snake_case: discount_id, offer_name, discount_type, discount_value,
 * start_date, end_date, created_by, updated_by, created_at, updated_at,
 * subscription_plan_ids, subscription_plan_names, subscription_plans.
 */
function normalizeOfferItem(raw: Record<string, unknown>): OfferListItem {
  const id = getStr(raw, 'id', 'id');
  const subscriptionPlans: OfferSubscriptionPlanRef[] = Array.isArray(
    raw['subscription_plans']
  )
    ? (raw['subscription_plans'] as unknown[]).map((p) => {
        const plan = p as Record<string, unknown>;
        return {
          id: String(plan['id'] ?? ''),
          name: String(plan['name'] ?? ''),
        };
      })
    : Array.isArray(raw['subscription_plan_ids'])
      ? (raw['subscription_plan_ids'] as string[]).map((planId, i) => {
          const names = raw['subscription_plan_names'] as string[] | undefined;
          return {
            id: String(planId),
            name: names?.[i] ?? String(planId),
          };
        })
      : [];

  const subscriptionPlanIds = subscriptionPlans.map((p) => p.id);

  const item: OfferListItem = {
    id,
    offerName:
      getStr(raw, 'offerName', 'offer_name') ||
      (raw['name'] != null ? String(raw['name']) : ''),
    discountId: getStr(raw, 'discountId', 'discount_id') || id,
    subscriptionPlans,
    subscriptionPlanIds,
    discountType:
      (getStr(raw, 'discountType', 'discount_type') || '').toUpperCase() ===
      'FIXED'
        ? 'FIXED'
        : 'PERCENTAGE',
    discountValue: getNum(raw, 'discountValue', 'discount_value'),
    startDate: getStr(raw, 'startDate', 'start_date'),
    endDate: getStr(raw, 'endDate', 'end_date'),
    status:
      (getStr(raw, 'status', 'status') || '') === 'expired'
        ? 'expired'
        : (getStr(raw, 'status', 'status') || '') === 'active'
          ? 'active'
          : 'inactive',
    createdAt: getStr(raw, 'createdAt', 'created_at'),
    updatedAt: getStr(raw, 'updatedAt', 'updated_at'),
  };
  const isExpired = getBool(raw, 'isExpired', 'is_expired');
  if (isExpired) {
    item.isExpired = true;
  }
  const createdBy = raw['created_by'] ?? raw['createdBy'];
  const updatedBy = raw['updated_by'] ?? raw['updatedBy'];
  if (createdBy !== undefined && createdBy !== null) {
    item.createdBy = createdBy as Exclude<
      OfferListItem['createdBy'],
      undefined
    >;
  }
  if (updatedBy !== undefined && updatedBy !== null) {
    item.updatedBy = updatedBy as Exclude<
      OfferListItem['updatedBy'],
      undefined
    >;
  }
  return item;
}

export const listOfferApiService = {
  listOffers: async (
    params?: ListOffersParams
  ): Promise<ListOffersResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    // Backend expects startDate/endDate, not validityPeriodStart/End
    if (params?.validityPeriodStart) {
      queryParams.append('startDate', params.validityPeriodStart);
    }
    if (params?.validityPeriodEnd) {
      queryParams.append('endDate', params.validityPeriodEnd);
    }
    // Backend expects planIds (comma-separated), not subscriptionPlanId
    if (params?.subscriptionPlanId) {
      queryParams.append('planIds', params.subscriptionPlanId);
    }
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const endpoint = `${OFFERS_LIST_ENDPOINT}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiService.getAPI(endpoint);

    if (response.error) {
      return {
        success: true,
        data: [],
        totalCount: 0,
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    const payload = response.data as Record<string, unknown>;
    const list = Array.isArray(payload['data']) ? payload['data'] : [];
    const pagination = (payload['pagination'] ?? {}) as Record<string, unknown>;
    const page = Number(pagination['page'] ?? params?.page ?? 1);
    const limit = Number(pagination['limit'] ?? params?.limit ?? 10);
    const totalCount = Number(pagination['totalCount'] ?? list.length);
    const totalPages = Number(
      pagination['totalPages'] ?? (Math.ceil(totalCount / limit) || 0)
    );
    const hasNextPage =
      typeof pagination['hasNextPage'] === 'boolean'
        ? pagination['hasNextPage']
        : page < totalPages;
    const hasPreviousPage =
      typeof pagination['hasPreviousPage'] === 'boolean'
        ? pagination['hasPreviousPage']
        : page > 1;

    return {
      success: true,
      data: (list as Record<string, unknown>[]).map((item) =>
        normalizeOfferItem(item)
      ),
      totalCount,
      page,
      limit,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    };
  },
};
